// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useMappedState } from "redux-react-hook";

// DRH Comp
import DrhInput from "common/comp/form/DrhInput";
import DrhSelect from "common/comp/form/DrhSelect";
import DrhCredential from "common/comp/form/DrhCredential";
import DrhRegion from "common/comp/form/DrhRegion";
import { EnumSpanType } from "common/InfoBar";
import DescLink from "common/comp/DescLink";

import {
  IRegionType,
  YES_NO,
  YES_NO_LIST,
  SOURCE_TYPE_OPTIONS_LAMBDA,
  SOURCE_TYPE_OPTIONS_EC2,
  S3_EVENT_TYPE,
  S3_EVENT_OPTIONS,
  S3_EVENT_OPTIONS_EC2,
  getRegionListBySourceType,
  METADATA_LINK,
  S3_EVENT_LINK,
  S3SourcePrefixType,
  S3SourcePrefixTypeList,
} from "assets/config/const";

import {
  ACTION_TYPE,
  EnumSourceType,
  S3_ENGINE_TYPE,
} from "assets/types/index";

import { IState } from "store/Store";
import Alert from "common/Alert";
const mapState = (state: IState) => ({
  tmpTaskInfo: state.tmpTaskInfo,
});

interface SourcePropType {
  engineType: string;
  srcShowBucketRequiredError: boolean;
  srcShowBucketValidError: boolean;
  srcRegionRequiredError: boolean;
  srcShowEndPointFormatError: boolean;
}

const SourceSettings: React.FC<SourcePropType> = (props) => {
  const {
    engineType,
    srcShowBucketRequiredError,
    srcShowBucketValidError,
    srcRegionRequiredError,
    srcShowEndPointFormatError,
  } = props;
  console.info("engineType:", engineType);
  const { t } = useTranslation();
  const { tmpTaskInfo } = useMappedState(mapState);
  const dispatch = useDispatch();
  console.info("tmpTaskInfo:", tmpTaskInfo);

  // Refs
  const srcBucketRef = useRef<any>(null);
  const srcRegionRef = useRef<any>(null);

  const [sourceType, setSourceType] = useState<string>(
    tmpTaskInfo?.parametersObj?.sourceType || EnumSourceType.S3
  );

  const [srcCredentialsParameterStore, setSrcCredentialsParameterStore] =
    useState(tmpTaskInfo?.parametersObj?.srcCredentialsParameterStore || "");

  const [sourceInAccount, setSourceInAccount] = useState<string>(
    tmpTaskInfo?.parametersObj?.sourceInAccount || YES_NO.NO
  );

  const [srcRegionObj, setSrcRegionObj] = useState<IRegionType | null>(
    tmpTaskInfo?.parametersObj?.srcRegionObj || null
  );

  // DRH S3 Dynamic Class
  const [sourceInAccountClass, setSourceInAccountClass] = useState("");
  const [s3EventClass, setS3EventClass] = useState("");
  const [showSrcCredential, setShowSrcCredential] = useState(true);
  const [showSrcRegion, setShowSrcRegion] = useState(true);

  const [srcEndpoint, setSrcEndpoint] = useState(
    tmpTaskInfo?.parametersObj?.srcEndpoint || ""
  );

  const [srcBucketName, setSrcBucketName] = useState(
    tmpTaskInfo?.parametersObj?.srcBucketName || ""
  );

  const [enableS3Event, setEnableS3Event] = useState<string>(
    tmpTaskInfo?.parametersObj?.enableS3Event || S3_EVENT_TYPE.NO
  );
  const [includeMetadata, setIncludeMetadata] = useState<string>(
    tmpTaskInfo?.parametersObj?.includeMetadata || YES_NO.NO
  );

  const [srcBucketRequiredError, setSrcBucketRequiredError] = useState(false);
  const [srcBucketFormatError, setSrcBucketFormatError] = useState(false);
  const [srcRegionReqired, setSrcRegionReqired] = useState(false);
  const [srcEndpointFormatError, setSrcEndpointFormatError] = useState(false);
  const [srcBucketPrefix, setSrcBucketPrefix] = useState(
    tmpTaskInfo?.parametersObj?.srcBucketPrefix
      ? decodeURIComponent(tmpTaskInfo?.parametersObj.srcBucketPrefix)
      : ""
  );

  const [regionList, setRegionList] = useState<IRegionType[]>([]);
  const [srcPrefixType, setSrcPrefixType] = useState<string>(
    tmpTaskInfo?.parametersObj?.srcPrefixType || S3SourcePrefixType.FullBucket
  );
  const [srcPrefixsListFile, setSrcPrefixsListFile] = useState(
    tmpTaskInfo?.parametersObj?.srcPrefixsListFile || ""
  );

  const [isPayerRequest, setIsPayerRequest] = useState(
    tmpTaskInfo?.parametersObj?.isPayerRequest || YES_NO.NO
  );

  // Monitor the sourceType change
  useEffect(() => {
    // Hide Errors
    setSrcBucketRequiredError(false);
    setSrcBucketFormatError(false);
    setSrcRegionReqired(false);
    // Update tmpTaskInfo
    updateTmpTaskInfo("sourceType", sourceType);
    // Set Endpoint to empty
    if (sourceType !== EnumSourceType.S3_COMPATIBLE) {
      setSrcEndpoint("");
    }
    // Set Is Bucket In Account to No
    if (sourceType !== EnumSourceType.S3) {
      setSourceInAccount(YES_NO.NO);
    }
    if (sourceType === EnumSourceType.S3) {
      setSourceInAccountClass("form-items");
    } else {
      setSourceInAccountClass("hidden");
      setS3EventClass("hidden");
    }
    if (sourceType === EnumSourceType.GoogleGCS) {
      setShowSrcRegion(false);
    } else {
      setShowSrcRegion(true);
    }
    setRegionList(getRegionListBySourceType(sourceType));
    if (sourceType === EnumSourceType.GoogleGCS) {
      updateTmpTaskInfo("srcRegionName", "Auto");
    }
    if (engineType === S3_ENGINE_TYPE.EC2) {
      if (sourceType === EnumSourceType.S3_COMPATIBLE) {
        setShowSrcRegion(false);
      } else {
        setShowSrcRegion(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceType]);

  // Show Error
  useEffect(() => {
    if (srcShowBucketRequiredError) {
      setSrcBucketRequiredError(srcShowBucketRequiredError);
    }
    if (srcShowBucketValidError) {
      setSrcBucketFormatError(srcShowBucketValidError);
    }
    if (srcShowBucketRequiredError || srcShowBucketValidError) {
      srcBucketRef?.current?.scrollIntoView();
    }
  }, [srcShowBucketRequiredError, srcShowBucketValidError]);

  useEffect(() => {
    if (srcRegionRequiredError) {
      setSrcRegionReqired(srcRegionRequiredError);
      srcRegionRef?.current?.scrollIntoView();
    }
  }, [srcRegionRequiredError]);

  useEffect(() => {
    setSrcEndpointFormatError(srcShowEndPointFormatError);
  }, [srcShowEndPointFormatError]);

  // Monitor the sourceInAccount Change
  useEffect(() => {
    updateTmpTaskInfo("sourceInAccount", sourceInAccount);
    if (sourceInAccount === YES_NO.YES) {
      setS3EventClass("form-items");
      setShowSrcCredential(false);
      setShowSrcRegion(false);
    } else {
      setS3EventClass("hidden");
      setShowSrcCredential(true);
      if (sourceType !== EnumSourceType.GoogleGCS) {
        setShowSrcRegion(true);
      }
    }
    if (engineType === S3_ENGINE_TYPE.EC2) {
      // If engine type is EC2 always show region
      setShowSrcRegion(true);
    }
  }, [sourceInAccount]);

  const updateTmpTaskInfo = (key: string, value: any) => {
    const param: any = { ...tmpTaskInfo?.parametersObj };
    param[key] = value;
    if (tmpTaskInfo) {
      dispatch({
        type: ACTION_TYPE.UPDATE_TASK_INFO,
        taskInfo: Object.assign(tmpTaskInfo, {
          parametersObj: param,
        }),
      });
    }
  };

  // Monitor Data Change
  useEffect(() => {
    updateTmpTaskInfo("srcEndpoint", srcEndpoint);
  }, [srcEndpoint]);

  useEffect(() => {
    updateTmpTaskInfo("srcBucketName", srcBucketName);
  }, [srcBucketName]);

  useEffect(() => {
    updateTmpTaskInfo("srcBucketPrefix", encodeURIComponent(srcBucketPrefix));
  }, [srcBucketPrefix]);

  useEffect(() => {
    updateTmpTaskInfo(
      "srcCredentialsParameterStore",
      srcCredentialsParameterStore
    );
    if (!srcCredentialsParameterStore) {
      setIsPayerRequest(YES_NO.NO);
    }
  }, [srcCredentialsParameterStore]);

  useEffect(() => {
    updateTmpTaskInfo("srcRegionObj", srcRegionObj);
    updateTmpTaskInfo("srcRegionName", srcRegionObj?.value || "");
  }, [srcRegionObj]);

  useEffect(() => {
    updateTmpTaskInfo("enableS3Event", enableS3Event);
  }, [enableS3Event]);

  useEffect(() => {
    updateTmpTaskInfo("includeMetadata", includeMetadata);
  }, [includeMetadata]);

  useEffect(() => {
    updateTmpTaskInfo("srcPrefixType", srcPrefixType);
  }, [srcPrefixType]);

  useEffect(() => {
    updateTmpTaskInfo("srcPrefixsListFile", srcPrefixsListFile);
  }, [srcPrefixsListFile]);

  useEffect(() => {
    // Reset src bucket prefix and src prefix list file
    if (srcPrefixType === S3SourcePrefixType.SinglePrefix) {
      setSrcPrefixsListFile("");
    }
    if (srcPrefixType === S3SourcePrefixType.MultiplePrefix) {
      setSrcBucketPrefix("");
    }
    if (srcPrefixType === S3SourcePrefixType.FullBucket) {
      setSrcPrefixsListFile("");
      setSrcBucketPrefix("");
    }
  }, [srcPrefixType]);

  useEffect(() => {
    updateTmpTaskInfo("isPayerRequest", isPayerRequest);
  }, [isPayerRequest]);

  return (
    <div className="box-shadow card-list">
      <div className="option">
        <div className="option-title">
          {t("creation.step2.settings.source.title")}
        </div>
        <div className="option-content">
          <div className="form-items">
            <DrhSelect
              isI18n={true}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setSrcRegionObj(null);
                setSourceType(event.target.value);
              }}
              optionTitle={t("creation.step2.sourceType")}
              optionDesc={t("creation.step2.selectSourceType")}
              selectValue={sourceType}
              optionList={
                engineType === S3_ENGINE_TYPE.EC2
                  ? SOURCE_TYPE_OPTIONS_EC2
                  : SOURCE_TYPE_OPTIONS_LAMBDA
              }
            />
          </div>

          {engineType === S3_ENGINE_TYPE.EC2 &&
            sourceType === EnumSourceType.S3_COMPATIBLE && (
              <div className="form-items">
                <DrhInput
                  optionTitle={t("creation.step2.settings.source.srcEndpoint")}
                  optionDesc={t(
                    "creation.step2.settings.source.srcEndpointDesc"
                  )}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setSrcEndpoint(event.target.value);
                  }}
                  inputName="srcEndpoint"
                  inputValue={srcEndpoint}
                  placeholder="https://s3-compatible-endpoint.com"
                  showFormatError={srcEndpointFormatError}
                  formatErrorMsg={t("tips.error.srcEndpointInvalid")}
                />
              </div>
            )}

          <div className="form-items" ref={srcBucketRef}>
            <DrhInput
              optionTitle={t("creation.step2.settings.source.bucketName")}
              optionDesc={t("creation.step2.settings.source.bucketDesc")}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setSrcBucketRequiredError(false);
                setSrcBucketFormatError(false);
                setSrcBucketName(event.target.value);
              }}
              inputName="srcBucketName"
              inputValue={srcBucketName}
              placeholder="src-bucket-name"
              showRequiredError={srcBucketRequiredError}
              requiredErrorMsg={t("tips.error.sourceBucketRequired")}
              showFormatError={srcBucketFormatError}
              formatErrorMsg={t("tips.error.sourceBucketNameInvalid")}
            />
          </div>

          <div className="form-items">
            <DrhSelect
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setSrcPrefixType(event.target.value);
              }}
              optionTitle={t("creation.step2.settings.source.transferType")}
              optionDesc=""
              selectValue={srcPrefixType}
              optionList={S3SourcePrefixTypeList}
            />
          </div>

          {srcPrefixType === S3SourcePrefixType.SinglePrefix && (
            <div className="form-items">
              <DrhInput
                showInfo={true}
                infoType={EnumSpanType.S3_BUCKET_SRC_PREFIX}
                optionTitle={t("creation.step2.settings.source.objectPrefix")}
                optionDesc={t("creation.step2.settings.source.prefixDesc")}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setSrcBucketPrefix(event.target.value);
                }}
                inputName="srcBucketPrefix"
                inputValue={srcBucketPrefix}
                placeholder="object-prefix/"
              />
            </div>
          )}

          {srcPrefixType === S3SourcePrefixType.MultiplePrefix && (
            <div className="form-items">
              <DrhInput
                showInfo={true}
                infoType={EnumSpanType.S3_BUCKET_SRC_PREFIX_LIST}
                optionTitle={t(
                  "creation.step2.settings.source.nameOfPrefixList"
                )}
                optionDesc={t(
                  "creation.step2.settings.source.nameOfPrefixListDesc"
                )}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setSrcPrefixsListFile(event.target.value);
                }}
                inputName="srcPrefixsListFile"
                inputValue={srcPrefixsListFile}
                placeholder="prefix_list.txt"
              />
            </div>
          )}

          <div className={sourceInAccountClass}>
            <DrhSelect
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setSourceInAccount(event.target.value);
              }}
              optionTitle={t("creation.step2.settings.source.srcInAccount")}
              optionDesc={t("creation.step2.settings.source.srcInAccountDesc")}
              selectValue={sourceInAccount}
              optionList={YES_NO_LIST}
            />
          </div>

          {showSrcCredential && (
            <div className="form-items">
              <DrhCredential
                credentialValue={srcCredentialsParameterStore}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setSrcCredentialsParameterStore(event.target.value);
                }}
              />
            </div>
          )}

          {sourceInAccount === YES_NO.NO && srcCredentialsParameterStore && (
            <div className="form-items">
              <DrhSelect
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setIsPayerRequest(event.target.value);
                }}
                optionTitle={t("creation.step2.settings.source.payerRequest")}
                optionDesc={t(
                  "creation.step2.settings.source.payerRequestDesc"
                )}
                selectValue={isPayerRequest}
                optionList={YES_NO_LIST}
              />
            </div>
          )}

          {showSrcRegion && (
            <>
              <div className="form-items" ref={srcRegionRef}>
                <DrhRegion
                  regionValue={srcRegionObj}
                  optionList={regionList}
                  optionTitle={t(
                    "creation.step2.settings.source.srcRegionName"
                  )}
                  optionDesc={t(
                    "creation.step2.settings.source.srcRegionNameDesc"
                  )}
                  showRequiredError={srcRegionReqired}
                  requiredErrorMsg={t("tips.error.srcRegionRequired")}
                  onChange={(
                    event: React.ChangeEvent<HTMLInputElement>,
                    data: IRegionType
                  ) => {
                    setSrcRegionReqired(false);
                    setSrcRegionObj(data);
                  }}
                />
                {tmpTaskInfo?.parametersObj?.srcRegionName?.startsWith(
                  "us-gov-"
                ) && (
                  <Alert
                    width={565}
                    content={t("creation.step2.settings.source.govCloudTips")}
                  />
                )}
              </div>
            </>
          )}

          <div className={s3EventClass}>
            <DrhSelect
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setEnableS3Event(event.target.value);
              }}
              optionTitle={t("creation.step2.settings.source.enableS3Event")}
              optionDesc={t("creation.step2.settings.source.enableS3EventDesc")}
              optionDescHtml={[
                t("creation.step2.settings.source.enableS3EventDesc1"),
                <DescLink
                  key={1}
                  link={S3_EVENT_LINK}
                  title={t("creation.step2.settings.source.enableS3EventDesc2")}
                />,
                t("creation.step2.settings.source.enableS3EventDesc3"),
              ]}
              selectValue={enableS3Event}
              optionList={
                engineType === S3_ENGINE_TYPE.EC2
                  ? S3_EVENT_OPTIONS_EC2
                  : S3_EVENT_OPTIONS
              }
            />
          </div>

          <div className="form-items">
            {engineType === S3_ENGINE_TYPE.EC2 && (
              <DrhSelect
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setIncludeMetadata(event.target.value);
                }}
                optionTitle={t(
                  "creation.step2.settings.source.includeMetadata"
                )}
                optionDesc=""
                optionDescHtml={[
                  t("creation.step2.settings.source.includeMetadataDesc1"),
                  <DescLink
                    key={1}
                    title={t(
                      "creation.step2.settings.source.includeMetadataDesc2"
                    )}
                    link={METADATA_LINK}
                  />,
                  t("creation.step2.settings.source.includeMetadataDesc3"),
                ]}
                selectValue={includeMetadata}
                optionList={YES_NO_LIST}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SourceSettings;
